/**
 * Container Form Page (Create & Edit)
 * Unified form component using custom hook and section components
 */

import { useParams, useNavigate } from 'react-router';
import { XCircle } from '@untitledui/icons';
import { BackLink } from '@/components/base/back-link/back-link';
import { useBackLink } from '@/hooks/use-back-link';
import { Input } from '@/components/base/input/input';
import { Button } from '@/components/base/buttons/button';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import {
    ContainerCreationProgressModal,
} from '@/components/application/containers/modals/container-creation-progress-modal';
import { useTranslation } from 'react-i18next';
import { useContainerForm } from '@/hooks/useContainerForm';
import { useCreateCompany } from '@/hooks/api/use-companies';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';

// Form Sections
import {
    ContainerInfoSection,
    RouteSection,
    VesselBookingSection,
    OrderSection,
    GoodsSection,
    PackingListSection,
    AdditionalInfoSection,
    ReceiverSection,
    IncotermsSection,
    DocumentsSection,
} from '@/components/application/containers/form-sections';

// ============================================================================
// COMPONENT
// ============================================================================

export const ContainerFormPage = () => {
    const { t } = useTranslation(['pages:containers', 'common', 'types']);
    const { id: containerId } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const backFallback = containerId ? `/shipper/containers/${containerId}` : '/shipper/containers';
    const { goBack } = useBackLink(backFallback);

    // Use custom hook for all form logic
    const {
        // State
        formData,
        isEditMode,
        isLoading,
        error,
        etaDate,
        setEtaDate,

        // Search States
        originPortSearch,
        setOriginPortSearch,
        destPortSearch,
        setDestPortSearch,
        hsCodeSearch,
        setHsCodeSearch,
        carrierSearch,
        setCarrierSearch,

        // Handlers
        handleInputChange,
        handlePortSelect,
        handleHsCodeSelect,
        handleSubmit,
        handleAddContainerNumber,
        handleRemoveContainerNumber,
        handleContainerNumberChange,

        // Transshipment ports
        transshipmentPortSearches,
        setTransshipmentPortSearch,
        transshipmentPortOptions,
        handleAddTransshipmentPort,
        handleRemoveTransshipmentPort,
        handleTransshipmentPortSelect,

        // File upload (solo create)
        selectedFiles,
        setSelectedFiles,
        handleFileDrop,
        handleRemoveFile,
        fileInputRef,
        isUploading,

        // Modals
        showAddCarrierModal,
        setShowAddCarrierModal,
        showInviteReceiverModal,
        setShowInviteReceiverModal,
        newCarrierName,
        setNewCarrierName,
        handleAddReceiver,
        handleAddCarrier,

        // Progress (solo create)
        showCreationProgress,
        setShowCreationProgress,
        creationProgress,
        setCreationProgress,

        // Options/Data
        statusOptions,
        cargoTypeOptions,
        transportModeOptions,
        containerTypeOptions,
        incotermsOptions,
        receiverOptions,
        carrierOptions,
        originPortOptions,
        destPortOptions,
        hsCodeOptions,
        orderOptions,
        currencyOptions,
        quantityUnitOptions,

        // Derived
        showTemperatureFields,
    } = useContainerForm({ containerId });

    // Create company mutation for modals
    const createCompany = useCreateCompany();

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-tertiary">{t('common.loading', 'Loading...')}</div>
            </div>
        );
    }


    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <BackLink fallbackTo={backFallback} aria-label={t('common.back', 'Back')} />
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">
                            {isEditMode ? t('form.editTitle', 'Edit Container') : t('form.title')}
                        </h1>
                        <p className="text-tertiary mt-1">
                            {isEditMode ? t('form.editSubtitle', 'Update container information') : t('form.subtitle')}
                        </p>
                    </div>
                </div>
                <ShipperOnboardingHeaderAction />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Error Message */}
                {error && (
                    <div className="rounded-lg bg-error-50 p-4 text-sm text-error-700">
                        {error}
                    </div>
                )}

                {/* Container Info Section */}
                <ContainerInfoSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    statusOptions={statusOptions}
                    containerNumbers={formData.containerNumbers}
                    onAddContainerNumber={handleAddContainerNumber}
                    onRemoveContainerNumber={handleRemoveContainerNumber}
                    onContainerNumberChange={handleContainerNumberChange}
                    isEditMode={isEditMode}
                />

                {/* Route Section */}
                <RouteSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    onPortSelect={handlePortSelect}
                    originPortSearch={originPortSearch}
                    setOriginPortSearch={setOriginPortSearch}
                    destPortSearch={destPortSearch}
                    setDestPortSearch={setDestPortSearch}
                    originPortOptions={originPortOptions}
                    destPortOptions={destPortOptions}
                    carrierOptions={carrierOptions}
                    carrierSearch={carrierSearch}
                    setCarrierSearch={setCarrierSearch}
                    etaDate={etaDate}
                    setEtaDate={setEtaDate}
                    onAddCarrier={() => setShowAddCarrierModal(true)}
                    transshipmentPorts={formData.transshipmentPorts}
                    transshipmentPortSearches={transshipmentPortSearches}
                    setTransshipmentPortSearch={setTransshipmentPortSearch}
                    transshipmentPortOptions={transshipmentPortOptions}
                    onAddTransshipmentPort={handleAddTransshipmentPort}
                    onRemoveTransshipmentPort={handleRemoveTransshipmentPort}
                    onTransshipmentPortSelect={handleTransshipmentPortSelect}
                    isEditMode={isEditMode}
                />

                {/* Vessel Info Section */}
                <VesselBookingSection
                    formData={formData}
                    onInputChange={handleInputChange}
                />

                {/* Order Section */}
                <OrderSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    orderOptions={orderOptions}
                />

                {/* Goods Section */}
                <GoodsSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    onHsCodeSelect={handleHsCodeSelect}
                    hsCodeSearch={hsCodeSearch}
                    setHsCodeSearch={setHsCodeSearch}
                    hsCodeOptions={hsCodeOptions}
                    quantityUnitOptions={quantityUnitOptions}
                    currencyOptions={currencyOptions}
                />

                {/* Packing List Section */}
                <PackingListSection
                    formData={formData}
                    onInputChange={handleInputChange}
                />

                {/* Additional Info Section */}
                <AdditionalInfoSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    transportModeOptions={transportModeOptions}
                    cargoTypeOptions={cargoTypeOptions}
                    containerTypeOptions={containerTypeOptions}
                    showTemperatureFields={showTemperatureFields}
                />

                {/* Receiver Section */}
                <ReceiverSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    receiverOptions={receiverOptions}
                    onAddReceiver={handleAddReceiver}
                    onInviteReceiver={() => setShowInviteReceiverModal(true)}
                />

                {/* Incoterms Section */}
                <IncotermsSection
                    formData={formData}
                    onInputChange={handleInputChange}
                    incotermsOptions={incotermsOptions}
                />

                {/* Documents Section (create mode only) */}
                {!isEditMode && (
                    <DocumentsSection
                        selectedFiles={selectedFiles}
                        onFilesChange={setSelectedFiles}
                        onFileDrop={handleFileDrop}
                        onRemoveFile={handleRemoveFile}
                        onAddFiles={() => fileInputRef.current?.click()}
                        fileInputRef={fileInputRef}
                        isUploading={isUploading}
                    />
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={goBack}
                        color="secondary"
                        size="lg"
                    >
                        {t('form.actions.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        size="lg"
                        isLoading={isUploading}
                    >
                        {isEditMode ? t('form.actions.update', 'Update Container') : t('form.actions.create')}
                    </Button>
                </div>
            </form>

            {/* Add Carrier Modal */}
            {showAddCarrierModal && (
                <ModalOverlay isOpen={showAddCarrierModal} onOpenChange={setShowAddCarrierModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('form.addCarrierTitle', 'Add Carrier')}</h2>
                                    <button
                                        onClick={() => setShowAddCarrierModal(false)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common.close', 'Close')}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <Input
                                        label={t('form.companyName', 'Company Name')}
                                        placeholder={t('form.companyNamePlaceholder', 'Enter company name...')}
                                        value={newCarrierName}
                                        onChange={setNewCarrierName}
                                        size="md"
                                        isRequired
                                    />
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        onClick={() => setShowAddCarrierModal(false)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('form.actions.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleAddCarrier}
                                        color="primary"
                                        size="md"
                                        isLoading={createCompany.isPending}
                                        isDisabled={!newCarrierName.trim()}
                                    >
                                        {t('form.actions.add', 'Add')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Invite Receiver Modal */}
            {showInviteReceiverModal && (
                <ModalOverlay isOpen={showInviteReceiverModal} onOpenChange={setShowInviteReceiverModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('form.inviteReceiverTitle', 'Invite Receiver')}</h2>
                                    <button
                                        onClick={() => setShowInviteReceiverModal(false)}
                                        className="text-tertiary hover:text-primary"
                                        aria-label={t('common.close', 'Close')}
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-tertiary">
                                    {t('form.inviteReceiverDescription', 'Invite receiver functionality coming soon. You will be able to send invitations to receivers to join the platform.')}
                                </p>
                                <div className="flex justify-end mt-6">
                                    <Button
                                        onClick={() => setShowInviteReceiverModal(false)}
                                        color="secondary"
                                        size="md"
                                    >
                                        {t('form.actions.close', 'Close')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Container Creation Progress Modal (create mode only) */}
            {!isEditMode && (
                <ContainerCreationProgressModal
                    isOpen={showCreationProgress}
                    onClose={() => {
                        // Navigate to the container if it was created (even with upload errors)
                        const navId = creationProgress.containerId;
                        setShowCreationProgress(false);
                        setCreationProgress({ step: 'saving' });
                        if (navId) {
                            navigate(`/shipper/containers/${navId}`);
                        }
                    }}
                    progress={creationProgress}
                />
            )}
        </div>
    );
};
